// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Link } from "react-router-dom";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction } from "actions";
import { IActionTypeSetPeriod, ActionGraphCommand, IActionChangeDashboardWidget } from "actions";
import { DashboardPanel, IDashboardEntry, MainPanel, TimePeriod } from "types";
import { isSet } from "utils";
import { toDropdownItems, getActiveDashboardItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";
import EnergyGraph from "view/comp/EnergyGraph";
import InputList from "view/comp/InputList";
import OutputList from "view/comp/OutputList";
import MonitorGraph from "view/comp/MonitorGraph";
import Alarm from "./Alarm";
import Reports from "./Reports";
import TariffCircuitList from "view/comp/TariffCircuitList";
import ControlCircuitSelect from "view/comp/ControlCircuitSelect";
import TrendMonitor from "./TrendMonitor";
import TrendMonitorSelect from "view/comp/TrendMonitorSelect";
import EMobilitySelect from "view/comp/EMobilitySelect";
import EMobilityWidget from "view/comp/EMobilityWidget";
import SiteWidget from "view/comp/SiteWidget";

import { ReactComponent as IconFullscreen } from "static/iconFullscreen.svg";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IDashboardProps {
  timePeriod: TimePeriod;
  showAlarms: boolean;
  showOcppChargers: boolean;
  showInputs: boolean;
  showOutputs: boolean;
  dashboardItems: (DashboardPanel | undefined)[];
  availableDashboards: IDashboardEntry[];
}

const mapStateToProps = (state: IState) => {
  const { dashboards, activeDashboardItems } = getActiveDashboardItems(state);

  const inputCount =
    state.parameter?.input?.filter((input) => {
      return input.webActive;
    }).length ?? 0;

  const outputCount =
    state.parameter?.output?.filter((output) => {
      return output.webActive;
    }).length ?? 0;

  const props: IDashboardProps = {
    timePeriod: state.gui.trendPeriod.timePeriod,
    showAlarms: (state.data?.alarms.length ?? 0) > 0,
    showOcppChargers: IS_CLOUD && state.ocpp.charger.length > 0,
    showInputs: inputCount > 0,
    showOutputs: outputCount > 0,
    dashboardItems: activeDashboardItems,
    availableDashboards: dashboards,
  };
  return props;
};

interface IDashboardDispatch {
  onPeriodChanged: (value: TimePeriod) => void;
  onDropdownSelectionChanged: (index: string, value: DashboardPanel | undefined) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IDashboardDispatch = {
    onPeriodChanged: (value: TimePeriod) => {
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        period: value,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      // load new graph data
      dispatch(raiseAction(ActionType.ACT_UPDATE_TREND));
    },
    onDropdownSelectionChanged: (index: string, value: DashboardPanel | undefined) => {
      const payload: IActionChangeDashboardWidget = {
        index: Number.parseInt(index),
        panel: value,
      };
      dispatch(raiseAction(ActionType.ACT_SELECT_DASHBOARD_WIDGET, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_period_caption: {
    id: "Select period",
  },
  msg_dashboard_items: {
    id: "Dashboard-Items",
  },
  msg_no_dashboard_item: {
    id: "Select Dashboard",
  },
});

type IDashboardWidgets = {
  [panel in DashboardPanel]: (widgetNr: number) => JSX.Element;
};

class DashboardComponent extends React.Component<
  IDashboardProps & IDashboardDispatch & WrappedComponentProps
> {
  private dashboarWidgets: IDashboardWidgets = {
    [DashboardPanel.EMPTY]: this.renderNoWidgetAvailable.bind(this),
    [DashboardPanel.ALARMS]: this.renderAlarmsWidget.bind(this),
    [DashboardPanel.EMOBILTY]: this.renderChargersWidget.bind(this),
    [DashboardPanel.ENERGY]: this.renderEnergyWidget.bind(this),
    [DashboardPanel.INPUTS]: this.renderInputListWidget.bind(this),
    [DashboardPanel.MONITOR]: this.renderMonitoringWidget.bind(this),
    [DashboardPanel.OUTPUTS]: this.renderOutputListWidget.bind(this),
    [DashboardPanel.POWER]: this.renderPowerWidget.bind(this),
    [DashboardPanel.REPORTS]: this.renderReportsWidget.bind(this),
    [DashboardPanel.SITEINFOS]: this.renderSiteInfosWidget.bind(this),
    [DashboardPanel.TRENDMONITOR]: this.renderTrendMonitorWidget.bind(this),
  };

  public render() {
    return (
      <div id="dashboard" className="row">
        <div className="dashboardpanel col-md-6 col-xxl-4">{this.renderDashboardWidget(0)}</div>
        <div className="dashboardpanel col-md-6 col-xxl-4">{this.renderDashboardWidget(1)}</div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-md-block">
          {this.renderDashboardWidget(2)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-md-block">
          {this.renderDashboardWidget(3)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-xxl-block">
          {this.renderDashboardWidget(4)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-xxl-block">
          {this.renderDashboardWidget(5)}
        </div>
      </div>
    );
  }

  private renderDashboardWidget(widgetIndex: number) {
    const widgetRenderer = this.props.dashboardItems[widgetIndex]
      ? this.dashboarWidgets[this.props.dashboardItems[widgetIndex]!]
      : undefined;
    if (isSet(widgetRenderer)) {
      return widgetRenderer(widgetIndex);
    } else {
      return this.renderNoWidgetAvailable(widgetIndex);
    }
  }

  private renderWidgetSelector(widgetNr: number) {
    let allOptions: (DashboardPanel | undefined)[] = Object.keys(
      this.dashboarWidgets,
    ) as DashboardPanel[];
    allOptions = allOptions
      .concat(
        undefined, // auto-select
      )
      .filter(
        (item) =>
          !isSet(item) ||
          item === DashboardPanel.EMPTY ||
          this.props.availableDashboards.some((availableItems) => availableItems.entry === item),
      )
      .sort((a, b) =>
        b === DashboardPanel.EMPTY
          ? -1 // empty selection shall be last
          : this.props.intl
              .formatMessage({ id: `DashboardPanel_${a}` })
              .localeCompare(this.props.intl.formatMessage({ id: `DashboardPanel_${b}` })),
      );
    const items = toDropdownItems(
      allOptions,
      `${widgetNr}`,
      this.props.onDropdownSelectionChanged,
      (value) => this.props.intl.formatMessage({ id: `DashboardPanel_${value ?? ""}` }),
    );

    return (
      <Dropdown
        className="me-1"
        items={items}
        caption={this.props.intl.formatMessage(messages.msg_dashboard_items)}
        curItem={
          this.props.dashboardItems[widgetNr]
            ? this.props.intl.formatMessage({
                id: `DashboardPanel_${this.props.dashboardItems[widgetNr]}`,
              })
            : this.props.intl.formatMessage(messages.msg_no_dashboard_item)
        }
      />
    );
  }

  private renderDashboardHeader(
    widgetNr: number,
    shouldRenderFSIcon: boolean,
    panel: MainPanel,
    renderOptionalSelector?: () => JSX.Element,
  ) {
    return (
      <div className="header">
        {this.renderWidgetSelector(widgetNr)}
        <div id="trendheading"></div>
        {isSet(renderOptionalSelector) && renderOptionalSelector()}
        {shouldRenderFSIcon && (
          <Link to={`../${panel}?widget=${widgetNr}`} className="icon-fullscreen">
            <IconFullscreen />
          </Link>
        )}
      </div>
    );
  }

  private renderPowerWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.POWER)}
        <TariffCircuitList dashboardIndex={widgetNr} filterText="" />
      </div>
    );
  }

  private renderReportsWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.REPORT)}
        <Reports isDashboard={true} isReadOnly={true} isAdminPage={false} />
      </div>
    );
  }

  private renderSiteInfosWidget(widgetNr: number) {
    return (
      <div style={{ height: "100%" }}>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.LOCAL_SETTINGS)}
        <SiteWidget />
      </div>
    );
  }

  private renderTrendMonitorWidget(widgetNr: number) {
    return (
      <div id="dashboardmonitor" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.TRENDMONITOR, () => (
          <TrendMonitorSelect dashboardIndex={widgetNr} />
        ))}
        <div className="flex-self flex-content graph-shrink">
          <TrendMonitor isDashboard={true} />
        </div>
      </div>
    );
  }

  private renderInputListWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, this.props.showInputs, MainPanel.IOS)}
        <InputList isEditable={false} isDashboard={true} />
      </div>
    );
  }
  private renderOutputListWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, this.props.showOutputs, MainPanel.IOS)}
        <OutputList isEditable={false} isDashboard={true} />
      </div>
    );
  }

  private renderAlarmsWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.ALARMS)}
        <Alarm isDashboard={true} />
      </div>
    );
  }

  private renderChargersWidget(widgetNr: number) {
    return (
      <div id="dashboardmonitor" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(
          widgetNr,
          this.props.showOcppChargers,
          MainPanel.CHARGERLIST,
          () => (
            <EMobilitySelect dashboardIndex={widgetNr} />
          ),
        )}
        <div className="flex-self flex-content graph-shrink">
          <EMobilityWidget dashboardIndex={widgetNr} />
        </div>
      </div>
    );
  }

  private renderEnergyWidget(widgetNr: number) {
    const periodOptions = [TimePeriod.DAY, TimePeriod.WEEK, TimePeriod.MONTH, TimePeriod.YEAR];
    const periodItems = toDropdownItems(periodOptions, "thisperiod", (_name, value) =>
      this.props.onPeriodChanged(value as any as TimePeriod),
    );

    return (
      <div id="dashboardenergy" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.ENERGY, () => (
          <>
            {/* <TariffCircuitSelect
              dashboardIndex={widgetNr}
              allowAllEnergySources={false}
              allowAllHierarchies={false}
              panel={DashboardPanel.ENERGY}
            /> */}
            {IS_CLOUD && (
              <div id="periodselect" className="d-none d-sm-block d-md-none d-lg-block">
                <Dropdown
                  items={periodItems}
                  caption={this.props.intl.formatMessage(messages.msg_period_caption)}
                  curItem={this.props.intl.formatMessage({
                    id: `thisperiod_${this.props.timePeriod ?? ""}`,
                  })}
                />
              </div>
            )}
          </>
        ))}
        <div className="flex-self flex-content graph-shrink">
          <EnergyGraph showLegend={false} dashboardIndex={widgetNr} />
        </div>
      </div>
    );
  }

  private renderMonitoringWidget(widgetNr: number) {
    return (
      <div id="dashboardmonitor" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.MONITOR, () => (
          <ControlCircuitSelect dashboardIndex={widgetNr} />
        ))}
        <div className="flex-self flex-content graph-shrink">
          <MonitorGraph dashboardIndex={widgetNr} />
        </div>
      </div>
    );
  }

  private renderNoWidgetAvailable(widgetNr: number) {
    return (
      <div>
        <div className="header">{this.renderWidgetSelector(widgetNr)}</div>
        <div id="trendheading"></div>
        <div>
          <FormattedMessage id="No widget selected." />
        </div>
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(DashboardComponent));
